/**
 * Mock data generators for forecast demo
 * Generates realistic contact center volume patterns with Monday peaks and weekend dips
 */

interface ForecastDataPoint {
  timestamp: Date;
  predictedValue: number;
  actualValue?: number;
  upperBound?: number;
  lowerBound?: number;
  interval: string;
}

interface ScheduledStaffing {
  time: string;
  agents: number;
}

/**
 * Generate realistic weekly contact volume pattern
 * Features: Monday morning peak, weekend dip, business hours curve
 */
export function generateWeeklyVolumePattern(baseVolume: number = 150): ForecastDataPoint[] {
  const data: ForecastDataPoint[] = [];
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);

  // Generate one week of data (7 days * 96 intervals per day)
  for (let day = 0; day < 7; day++) {
    for (let hour = 0; hour < 24; hour++) {
      for (let quarterHour = 0; quarterHour < 4; quarterHour++) {
        const timestamp = new Date(startDate);
        timestamp.setDate(timestamp.getDate() + day);
        timestamp.setHours(hour, quarterHour * 15, 0, 0);

        let volume = baseVolume;

        // Weekend dip (Saturday/Sunday: 40% of weekday volume)
        const dayOfWeek = timestamp.getDay();
        if (dayOfWeek === 0 || dayOfWeek === 6) {
          volume *= 0.4;
        }

        // Monday morning peak (9am-11am: 180% of base)
        if (dayOfWeek === 1 && hour >= 9 && hour < 11) {
          volume *= 1.8;
        }

        // Business hours curve (9am-5pm peak)
        if (hour >= 9 && hour < 17) {
          volume *= 1.2;

          // Lunch dip (12pm-1pm: -20%)
          if (hour === 12) {
            volume *= 0.8;
          }
        } else if (hour < 9 || hour >= 17) {
          // Off-hours reduction
          volume *= 0.6;
        }

        // Add realistic noise (±10%)
        const noise = 0.9 + Math.random() * 0.2;
        volume *= noise;

        const predicted = Math.round(volume);

        // For demo: add actual values with slight variance (±5%)
        const actualNoise = 0.95 + Math.random() * 0.1;
        const actual = Math.round(predicted * actualNoise);

        // Confidence intervals (±15%)
        const upper = Math.round(predicted * 1.15);
        const lower = Math.round(predicted * 0.85);

        const timeStr = `${hour.toString().padStart(2, '0')}:${(quarterHour * 15).toString().padStart(2, '0')}`;

        data.push({
          timestamp,
          predictedValue: predicted,
          actualValue: actual,
          upperBound: upper,
          lowerBound: lower,
          interval: timeStr,
        });
      }
    }
  }

  return data;
}

/**
 * Generate mock scheduled staffing data
 * Simulates typical 8-hour shifts with coverage
 */
export function generateScheduledStaffing(targetCoverage: number = 0.9): ScheduledStaffing[] {
  const staffing: ScheduledStaffing[] = [];

  // Generate one day of staffing (24 hours * 4 intervals)
  for (let hour = 0; hour < 24; hour++) {
    for (let quarterHour = 0; quarterHour < 4; quarterHour++) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${(quarterHour * 15).toString().padStart(2, '0')}`;

      let agents = 0;

      // Early shift (7am-3pm): 15 agents
      if (hour >= 7 && hour < 15) {
        agents += 15;
      }

      // Mid shift (9am-5pm): 50 agents
      if (hour >= 9 && hour < 17) {
        agents += 50;
      }

      // Late shift (1pm-9pm): 20 agents
      if (hour >= 13 && hour < 21) {
        agents += 20;
      }

      // Evening shift (5pm-1am): 10 agents
      if (hour >= 17 || hour < 1) {
        agents += 10;
      }

      // Weekend coverage (reduced)
      const now = new Date();
      const dayOfWeek = now.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        agents = Math.round(agents * 0.4);
      }

      // Add some variance (±10% to simulate imperfect scheduling)
      const variance = 0.9 + Math.random() * 0.2;
      agents = Math.round(agents * variance * targetCoverage);

      staffing.push({ time: timeStr, agents });
    }
  }

  return staffing;
}

/**
 * Generate simple volume data for shape editor
 */
export function generateSimpleVolumeData(): Array<{ time: string; volume: number }> {
  const data: Array<{ time: string; volume: number }> = [];

  for (let hour = 0; hour < 24; hour++) {
    for (let quarterHour = 0; quarterHour < 4; quarterHour++) {
      const timeStr = `${hour.toString().padStart(2, '0')}:${(quarterHour * 15).toString().padStart(2, '0')}`;

      let volume = 100;

      // Business hours pattern
      if (hour >= 9 && hour < 17) {
        volume = 150;
      }

      // Add some variance
      volume += Math.round((Math.random() - 0.5) * 20);

      data.push({ time: timeStr, volume });
    }
  }

  return data;
}

/**
 * Generate historical comparison data (12 weeks)
 */
export function generateHistoricalComparison() {
  const weeks = 12;
  const historicalAverage = {
    mape: 12.5,
    mae: 18.3,
    accuracy: 87.5,
  };

  const linearRegression = {
    mape: 9.2,
    mae: 14.1,
    accuracy: 90.8,
  };

  return {
    historicalAverage,
    linearRegression,
    recommended: linearRegression.mape < historicalAverage.mape ? 'LINEAR_REGRESSION' : 'HISTORICAL_AVERAGE',
    weeksAnalyzed: weeks,
  };
}

/**
 * Generate demo forecast with perfect Monday 9am (all green)
 * Then we can add a spike to show the "magic moment"
 */
export function generatePerfectMondayForecast() {
  const forecastData = generateWeeklyVolumePattern(150);
  const staffingData = generateScheduledStaffing(1.0); // 100% coverage

  // Filter for Monday only
  const mondayForecast = forecastData.filter((point) => {
    return point.timestamp.getDay() === 1;
  });

  return {
    forecastData: mondayForecast,
    scheduledData: staffingData,
  };
}

/**
 * Add a spike to the forecast at specified time
 */
export function addSpikeToForecast(
  data: ForecastDataPoint[],
  targetHour: number,
  spikePercent: number
): ForecastDataPoint[] {
  return data.map((point) => {
    const hour = point.timestamp.getHours();

    if (hour === targetHour) {
      return {
        ...point,
        predictedValue: Math.round(point.predictedValue * (1 + spikePercent / 100)),
        upperBound: point.upperBound ? Math.round(point.upperBound * (1 + spikePercent / 100)) : undefined,
        lowerBound: point.lowerBound ? Math.round(point.lowerBound * (1 + spikePercent / 100)) : undefined,
      };
    }

    return point;
  });
}
